import { useState } from 'react';
import { Input, Message, toaster } from 'rsuite';
import { Button as NsiButton, Modal, useLastPresent } from '@nsi-advisory-services/components';

import { useAddInvite, useInvites, useRemoveInvite } from '../hooks/useQueries';

// API errors read "HTTP <status>: <body>"; show the server's message when it sent one.
export const apiErrorMessage = (err, fallback) => {
  try {
    const body = JSON.parse(String(err?.message || '').replace(/^HTTP \d+: /, ''));
    return body.message || body.error || fallback;
  } catch {
    return fallback;
  }
};

export const notify = (type, text) => {
  toaster.push(
    <Message type={type} showIcon>
      {text}
    </Message>,
    { placement: 'topEnd', duration: type === 'error' ? 4500 : 2500 }
  );
};

export default function UserInvites() {
  const { data: invites, isLoading, error } = useInvites();
  const addInvite = useAddInvite();
  const removeInvite = useRemoveInvite();
  const [email, setEmail] = useState('');
  // The list starts folded away; inviting someone opens it so the new row shows.
  const [expanded, setExpanded] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const confirmRowShown = useLastPresent(confirmRow); // keeps the dialog steady while it fades out

  const rows = Array.isArray(invites) ? invites : [];
  const pendingCount = rows.filter((row) => !row.accepted_at).length;

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    try {
      await addInvite.mutateAsync(value);
      setEmail('');
      setExpanded(true);
      notify('success', `Invited ${value}.`);
    } catch (err) {
      notify('error', apiErrorMessage(err, 'Failed to add invite.'));
    }
  };

  const remove = async () => {
    try {
      await removeInvite.mutateAsync(confirmRow.id);
      setConfirmRow(null);
      notify('success', 'Invite removed.');
    } catch (err) {
      notify('error', apiErrorMessage(err, 'Failed to remove invite.'));
    }
  };

  return (
    <section className="ppc-cfg-section">
      <div className="ppc-cfg-section__head">
        <div className="ppc-cfg-section__heading">
          <h2 className="ppc-cfg-section__title">
            <button
              type="button"
              className="ppc-cfg-section__toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="ppc-invites-table"
            >
              <i className={`bi bi-chevron-right ppc-collapse-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true" />
              Invites
            </button>
          </h2>
          {pendingCount > 0 ? <span className="ppc-cfg-count">{pendingCount}</span> : null}
        </div>

        <form className="ppc-cfg-section__actions" onSubmit={submit}>
          <Input
            size="sm"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="name@company.com"
            aria-label="Email to invite"
            style={{ width: 240 }}
          />
          <button
            type="submit"
            className="ppc-cfg-btn ppc-cfg-btn--sm ppc-cfg-btn--primary"
            disabled={addInvite.isPending || !email.trim()}
          >
            Invite
            <i className="bi bi-plus-lg" aria-hidden="true" />
          </button>
        </form>
      </div>

      {error ? (
        <Message type="error" showIcon>
          Failed to load invites.
        </Message>
      ) : null}

      <div id="ppc-invites-table" className={`ppc-collapse${expanded ? ' is-open' : ''}`} inert={!expanded}>
        <div className="ppc-collapse__inner">
          <div className="nsi-table-wrap">
            <table className="nsi-table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Invited by</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="nsi-table__right">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td className="nsi-table__empty" colSpan={4}>Loading invites…</td>
                  </tr>
                ) : null}

                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td className="nsi-table__empty" colSpan={4}>No invites yet.</td>
                  </tr>
                ) : null}

                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.email}</td>
                    <td>{row.invited_by || '—'}</td>
                    <td>{row.accepted_at ? 'Accepted' : 'Pending'}</td>
                    <td className="nsi-table__right">
                      {row.accepted_at ? null : (
                        <button type="button" className="ppc-cfg-btn ppc-cfg-btn--sm" onClick={() => setConfirmRow(row)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal open={Boolean(confirmRow)} onClose={() => setConfirmRow(null)} size="sm">
        <Modal.Header>
          <Modal.Title>Remove invite?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <strong>{confirmRowShown?.email}</strong> will not be able to sign in to ProPlan Connect.
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setConfirmRow(null)} disabled={removeInvite.isPending}>
            Cancel
          </NsiButton>
          <NsiButton variant="danger-solid" loading={removeInvite.isPending} onClick={remove}>
            Remove
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
